import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type { Profile } from '../types/profile';

type AuthContextValue = {
  currentUser: User | null;
  userProfile: Profile | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  /** True when profile status is pending deletion (grace period). */
  isDeletionPending: boolean;
  /** ISO timestamp when the account will be deleted, if scheduled. */
  deletionScheduledAt: string | null;
  /** True when onboarding wizard is not required (profile flag from Supabase). */
  isOnboardingComplete: boolean;
};

/** Loads full profile row for the shell/dashboard (no app-level status gating — Supabase ban + login UX handle access). */
async function fetchProfileForSession(userId: string): Promise<{
  profile: Profile | null;
  errorMessage: string | null;
}> {
  const timeoutPromise = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error('Profile fetch timeout')), 5000)
  );

  try {
    const result = await Promise.race([
      supabase.from('profiles').select('*').eq('id', userId).maybeSingle(),
      timeoutPromise,
    ]);

    const { data, error } = result;
    if (error) {
      return { profile: null, errorMessage: error.message };
    }
    return { profile: (data ?? null) as Profile | null, errorMessage: null };
  } catch {
    return { profile: null, errorMessage: null };
  }
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const resolveSession = useCallback(async (session: Session | null) => {
    if (!session?.user) {
      setCurrentUser(null);
      setUserProfile(null);
      return;
    }

    const { profile, errorMessage } = await fetchProfileForSession(session.user.id);
    if (errorMessage) {
      console.error('[Auth] profile fetch failed:', errorMessage);
      setCurrentUser(session.user);
      setUserProfile(null);
      return;
    }

    setCurrentUser(session.user);
    setUserProfile(profile);
  }, []);

  useEffect(() => {
    let mounted = true;

    void (async () => {
      try {
        const sessionResult = await supabase.auth.getSession();
        const session = sessionResult?.data?.session ?? null;
        if (!mounted) return;
        await resolveSession(session);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    const { data } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;
      if (event === 'INITIAL_SESSION') return;

      if (event === 'SIGNED_OUT' || !session?.user) {
        setCurrentUser(null);
        setUserProfile(null);
        return;
      }

      await resolveSession(session);
    });

    return () => {
      mounted = false;
      data.subscription.unsubscribe();
    };
  }, [resolveSession]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const refreshProfile = useCallback(async () => {
    const sessionResult = await supabase.auth.getSession();
    const session = sessionResult?.data?.session ?? null;
    if (session?.user) await resolveSession(session);
    else {
      setCurrentUser(null);
      setUserProfile(null);
    }
  }, [resolveSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      currentUser,
      userProfile,
      loading,
      signOut,
      refreshProfile,
      isDeletionPending: userProfile?.status === 'pending_deletion',
      deletionScheduledAt: userProfile?.deletion_scheduled_at ?? null,
      isOnboardingComplete: userProfile?.onboarding_completed === true,
    }),
    [currentUser, userProfile, loading, signOut, refreshProfile]
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
